import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import styles from "./HeaderNavImproved.module.scss"
import { connect } from "react-redux"
import { showJobboardTrialActivationBanner } from "../../../utils/jobboard/jobboardProfileGate.ts"
import Logo from "../../../assets/images/LogoEdify1.png"
import NotificationsImproved from "../../Notifications/NotificationsImproved.tsx"
import { useNavigate, useLocation } from "react-router-dom"
import { ReactComponent as Faq } from "../../../assets/svg/FAQ.svg"
import { ReactComponent as FaqActive } from "../../../assets/svg/FAQActive.svg"
import { ReactComponent as NoImage } from "../../../assets/svg/noImageIcon.svg"
import { User, LogOut, ArrowLeft, MessageSquare, UserPlus, Sparkles } from "lucide-react"
import {
  closeSuscriptionModal,
  setActivateFreeTrial,
  logout,
  openMainModal,
  closeMainModal,
  openFeedTextModal,
  openSuscriptionModal,
  upgradeJobboardToClientAndActivateTrial,
} from "../../../store/actions"
import Tooltip from "../../UI/ToolTip/ToolTip"
import useUserImage from "../../../hooks/useUserImage"
import { buildProfileUrl, getUserDisplayName, PROFILE_SUBITEM_TAB_MAPPING } from "../../../utils/navigationHelpers.ts"

interface HeaderNavSubItem {
  id: string
  label: string
  description: string
  route: string
}

interface HeaderNavSection {
  id: string
  label: string
  adminOnly?: boolean
  subItems: HeaderNavSubItem[]
}

interface HeaderNavImprovedProps {
  userData: any
  isGuestAccount: boolean
  logout: (withAnimation: boolean, navigate: any) => void
  openMainModal: (config: any) => void
  closeMainModal: () => void
  openFeedTextModal?: (config: { text: boolean }) => void
  openSuscriptionModal: () => void
  upgradeJobboardToClientAndActivateTrial: () => unknown
  projectDocumentation: any
  isConversationalModeActive?: boolean
  onCreateNewSession?: () => void
  onOpenSearchModal?: () => void
  onCloseConversationalMode?: () => void
  hasActiveConversation?: boolean
  totalChatsCount?: number
  isSidebarCollapsed?: boolean
}

const HEADER_NAV_SECTIONS: HeaderNavSection[] = [
  {
    id: 'publicar',
    label: 'Publicar',
    subItems: [
      { id: 'presupuestos-obra', label: 'Presupuestos para mi obra', description: 'Crea y gestiona el presupuesto de tu obra privada.', route: '/obrasprivadas/crear' },
      { id: 'cotizar-materiales', label: 'Pedir una cotización', description: 'Solicita cotizaciones de materiales a proveedores.', route: '/cotizaciondemateriales/crear' },
      { id: 'publicar-oferta-trabajo', label: 'Vacante laboral', description: 'Publicá una vacante para que profesionales se postulen a tu búsqueda.', route: '/mercado-de-trabajo/crearoferta' },
      { id: 'ofrecer-servicios', label: 'Publicar posteos o productos/servicios', description: 'Publica posteos, productos o servicios para que la comunidad los descubra.', route: '#' },
    ],
  },
  {
    id: 'explorar',
    label: 'Explorar',
    subItems: [
      { id: 'cotizar-obras', label: 'Cotizar en obras privadas', description: 'Encuentra obras privadas donde puedas cotizar.', route: '/obrasprivadas/explorar' },
      { id: 'obras-publicas', label: 'Obras públicas', description: 'Explora licitaciones y obras públicas.', route: '/obraspublicas' },
      { id: 'cotizaciones-materiales', label: 'Cotizaciones de materiales', description: 'Busca y responde a pedidos de cotización de materiales, públicos y privados.', route: '/cotizaciondemateriales/explorar' },
      { id: 'encontrar-profesionales', label: 'Perfiles', description: 'Encuentra profesionales por especialidad o zona.', route: '/buscar-perfiles' },
      { id: 'explorar-servicios', label: 'Posteos, servicios y productos', description: 'Descubre posteos, servicios y productos de la comunidad.', route: '/marketplace/explorar' },
      { id: 'postularme-ofertas', label: 'Postularme a ofertas de trabajo', description: 'Encuentra ofertas de trabajo y postúlate.', route: '/bolsa-trabajo' },
    ],
  },
  {
    id: 'mis-contenidos',
    label: 'Mis contenidos',
    subItems: [
      { id: 'mis-proyectos', label: 'Mis proyectos en Edify', description: 'Accede a tus proyectos y documentación.', route: '#' },
      { id: 'mis-cotizaciones-materiales', label: 'Mis cotizaciones de materiales', description: 'Revisa tus cotizaciones enviadas y recibidas.', route: '#' },
      { id: 'mis-postulaciones', label: 'Mis postulaciones', description: 'Sigue el estado de tus postulaciones a obras privadas.', route: '#' },
      { id: 'mis-productos-servicios', label: 'Mis productos/servicios', description: 'Gestiona tus publicaciones en el marketplace.', route: '#' },
    ],
  },
  {
    id: 'herramientas',
    label: 'Herramientas',
    subItems: [
      { id: 'estimacion-costos', label: 'Estimación de costos', description: 'Calculá los costos de materiales para tu obra.', route: '/cotizaciondemateriales/estimacion-costos' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    adminOnly: true,
    subItems: [
      { id: 'backoffice', label: 'Backoffice', description: 'Panel de administración de Edify.', route: '/backoffice' },
    ],
  },
]

const HeaderNavImproved: React.FC<HeaderNavImprovedProps> = ({
  userData,
  isGuestAccount,
  logout,
  openMainModal,
  closeMainModal,
  openFeedTextModal,
  openSuscriptionModal,
  upgradeJobboardToClientAndActivateTrial,
  projectDocumentation,
  isConversationalModeActive = false,
  onCreateNewSession,
  onOpenSearchModal,
  onCloseConversationalMode,
  hasActiveConversation = false,
  totalChatsCount = 0,
  isSidebarCollapsed = false,
}) => {
  const [isActive, setIsActive] = useState<string | null>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const showJobboardTrialHint =
    !isGuestAccount && showJobboardTrialActivationBanner(userData)

  const userAvatarTooltip = isGuestAccount
    ? "Cuenta invitado – Crear cuenta gratis"
    : showJobboardTrialHint
      ? "Cuenta bolsa de trabajo – Activá el free trial cuando quieras"
      : (userData?.username ?? "")
  const [showToolTip, setShowToolTip] = useState({
    faq: { show: false, text: "Ayuda" },
    notifications: { show: false, text: "Notificaciones" },
    historyButton: { show: false, text: "Mis conversaciones" },
    userAvatar: { show: false },
  })
  
  const navigate = useNavigate()
  const location = useLocation()
  const userMenuRef = useRef<HTMLDivElement>(null)
  const navDropdownsRef = useRef<HTMLDivElement>(null)
  const closeDropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isJobboardUpgradeLoading, setIsJobboardUpgradeLoading] = useState(false)

  const DROPDOWN_CLOSE_DELAY = 180

  const clearDropdownCloseTimeout = () => {
    if (closeDropdownTimeoutRef.current) {
      clearTimeout(closeDropdownTimeoutRef.current)
      closeDropdownTimeoutRef.current = null
    }
  }

  const scheduleDropdownClose = () => {
    clearDropdownCloseTimeout()
    closeDropdownTimeoutRef.current = setTimeout(() => setOpenDropdownId(null), DROPDOWN_CLOSE_DELAY)
  }
  
  const { imageError, initials, profileImage } = useUserImage(userData)

  const isDashboardRoute = location.pathname === '/dashboard'
  const isProjectDocumentationRoute = location.pathname.includes('/documentacion-de-obra')
  const showHeaderNavDropdowns = isDashboardRoute && !isConversationalModeActive
  const isUserAdmin = userData?.rols?.some((r: any) => r.name === "ADMIN")
  const headerNavSections = useMemo(() => 
    HEADER_NAV_SECTIONS.filter(s => !s.adminOnly || isUserAdmin),
    [isUserAdmin]
  )
  
  // Obtener nombres de las selecciones del proyecto
  const projectType = projectDocumentation?.selectedType
  const categoryName = projectDocumentation?.categories?.find((cat: any) => cat.id === projectDocumentation?.selectedCategory)?.name
  const subCategoryName = projectDocumentation?.subCategories?.find((sub: any) => sub.id === projectDocumentation?.selectedSubCategory)?.name
  const useName = projectDocumentation?.uses?.find((use: any) => use.id === projectDocumentation?.selectedUses?.[0])?.name
  const materialityName = projectDocumentation?.materialities?.find((mat: any) => mat.id === projectDocumentation?.selectedMaterialities?.[0])?.name
  
  // Mostrar resumen cuando Redux lo indique (activado por scroll en ProjectForm)
  const showSummary = isProjectDocumentationRoute && projectDocumentation?.showHeaderSummary && (projectType || categoryName || subCategoryName || useName || materialityName)

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node
    if (userMenuRef.current && !userMenuRef.current.contains(target)) {
      setShowUserMenu(false)
    }
    if (navDropdownsRef.current && !navDropdownsRef.current.contains(target)) {
      clearDropdownCloseTimeout()
      setOpenDropdownId(null)
    }
  }

  useEffect(() => {
    if (showUserMenu || openDropdownId) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showUserMenu, openDropdownId])

  useEffect(() => {
    return () => clearDropdownCloseTimeout()
  }, [])

  const handleHeaderNavSubItemClick = (subItem: HeaderNavSubItem) => {
    clearDropdownCloseTimeout()
    setOpenDropdownId(null)
    if (subItem.id === 'ofrecer-servicios') {
      openFeedTextModal?.({ text: true })
      return
    }
    if (subItem.route === '#') {
      const profileTab = PROFILE_SUBITEM_TAB_MAPPING[subItem.id]
      if (profileTab && userData?.id) {
        const displayName = getUserDisplayName(userData) || 'perfil'
        const url = buildProfileUrl(String(userData.id), displayName, profileTab)
        navigate(url, { state: { fromDashboard: true } })
      }
      return
    }
    navigate(subItem.route, { state: { fromDashboard: true } })
  }


  const userName = userData?.type === "COMPANY" 
    ? userData?.razon_social 
    : `${userData?.name} ${userData?.last_name}`

  const isViewingOwnProfile = useMemo(() => {
    if (!userData?.id) return false
    const pathParts = location.pathname.split("/").filter(Boolean)
    if (pathParts[0] !== "perfil") return false
    if (!pathParts[1]) return false
    return pathParts[1] === String(userData.id)
  }, [location.pathname, userData?.id])

  const handleRedirectToProfile = () => {
    setShowUserMenu(false)
    navigate(`/perfil/${userData.id}`)
  }

  const handleRedirect = (title: string) => {
    if (title === "FAQ") {
      navigate("/faq")
    }
  }

  const handleCloseSesion = () => {
    setShowUserMenu(false)
    openMainModal({
      title: "¿Deseas cerrar la sesión activa?",
      body: "Dejarán de llegarte las notificaciones de actividad",
      isSuccess: false,
      iconType: "warning",
      confirmHandler: () => {
        logout(true, navigate)
        closeMainModal()
      },
    })
  }

  const handleBackToHome = () => {
    if (location?.pathname !== "/dashboard") {
      navigate("/dashboard")
    }
  }

  const handleTooltip = (tooltip: string) => {
    setShowToolTip((prevState) => ({
      ...prevState,
      [tooltip]: { ...prevState[tooltip as keyof typeof prevState], show: !prevState[tooltip as keyof typeof prevState].show },
    }))
  }

  useEffect(() => {
    if (location?.pathname === "/faq") {
      setIsActive("FAQ")
    } else if (location?.pathname === "/notificaciones") {
      setIsActive("notificaciones")
    } else if (location?.pathname.startsWith("/perfil")) {
      const pathParts = location.pathname.split("/")
      const idInPath = pathParts[2]
      if (idInPath && userData?.id && String(idInPath) === String(userData.id)) {
        setIsActive("Perfil")
      } else {
        setIsActive(null)
      }
    } else {
      setIsActive(null)
    }
  }, [location?.pathname, userData?.id])

  const hasSidebar = isConversationalModeActive && isDashboardRoute

  const handleJobboardUpgrade = useCallback(() => {
    setIsJobboardUpgradeLoading(true)
    Promise.resolve(upgradeJobboardToClientAndActivateTrial()).finally(() =>
      setIsJobboardUpgradeLoading(false)
    )
  }, [upgradeJobboardToClientAndActivateTrial])

  return (
    <nav className={`${styles.nav} ${hasSidebar ? styles.withSidebar : ''} ${hasSidebar && isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
      <div className={styles.navBody}>
        <div className={styles.navLeft}>
          {!hasSidebar && (
            <div className={styles.logoContainer} onClick={handleBackToHome}>
              <img src={Logo} alt="Logo" className={styles.logo} />
            </div>
          )}
          {showHeaderNavDropdowns && (
            <div ref={navDropdownsRef} className={styles.navDropdowns}>
              {headerNavSections.map((section, index) => (
                <React.Fragment key={section.id}>
                  <div className={styles.navDropdownWrap}>
                    <button
                      type="button"
                      className={`${styles.navDropdownTrigger} ${openDropdownId === section.id ? styles.navDropdownTriggerOpen : ''}`}
                      onClick={() => {
                        clearDropdownCloseTimeout()
                        setOpenDropdownId(prev => prev === section.id ? null : section.id)
                      }}
                      onMouseEnter={() => {
                        clearDropdownCloseTimeout()
                        setOpenDropdownId(section.id)
                      }}
                      onMouseLeave={scheduleDropdownClose}
                      aria-expanded={openDropdownId === section.id}
                      aria-haspopup="true"
                      aria-label={section.label}
                    >
                      {section.label}
                    </button>
                    {openDropdownId === section.id && (
                      <div
                        className={styles.navDropdownPanel}
                        onMouseEnter={clearDropdownCloseTimeout}
                        onMouseLeave={scheduleDropdownClose}
                      >
                        {section.subItems.map((subItem) => (
                          <button
                            key={subItem.id}
                            type="button"
                            className={styles.navDropdownItem}
                            onClick={() => handleHeaderNavSubItemClick(subItem)}
                          >
                            <span className={styles.navDropdownItemTitle}>{subItem.label}</span>
                            <span className={styles.navDropdownItemDescription}>{subItem.description}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {index < headerNavSections.length - 1 && (
                    <span className={styles.navDropdownDivider} aria-hidden />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Resumen de Proyecto - Solo visible en ruta de documentación */}
        {isProjectDocumentationRoute && (projectType || categoryName || subCategoryName || useName || materialityName) && (
          <div className={`${styles.summaryCenter} ${showSummary ? styles.visible : styles.hidden}`}>
            <div className={styles.summaryContent}>
              {projectType && (
                <>
                  <span className={styles.summaryLabel}>Tipo:</span>
                  <span className={styles.summaryValue}>{projectType === 'proyecto' ? 'Proyecto' : 'Anteproyecto'}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {categoryName && (
                <>
                  <span className={styles.summaryLabel}>Cat:</span>
                  <span className={styles.summaryValue}>{categoryName}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {subCategoryName && (
                <>
                  <span className={styles.summaryLabel}>Sub:</span>
                  <span className={styles.summaryValue}>{subCategoryName}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {useName && (
                <>
                  <span className={styles.summaryLabel}>Uso:</span>
                  <span className={styles.summaryValue}>{useName}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {materialityName && (
                <>
                  <span className={styles.summaryLabel}>Mat:</span>
                  <span className={styles.summaryValue}>{materialityName}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Botón de historial - Visible cuando NO está en modo conversacional pero tiene chats */}
        {/* {isDashboardRoute && !isConversationalModeActive && totalChatsCount > 0 && onOpenSearchModal && (
          <div className={styles.historyButtonNav}>
            <button
              className={styles.historyButtonNavBtn}
              onClick={onOpenSearchModal}
              type="button"
              aria-label="Ver conversaciones anteriores"
            >
              <MessageSquare size={20} className={styles.historyButtonIcon} />
              <span className={styles.historyBadgeNav}>{totalChatsCount}</span>
            </button>
          </div>
        )} */}

        {/* Leyenda de tecnología - Solo visible en modo conversacional */}
        {isDashboardRoute && isConversationalModeActive && (
          <div className={styles.aiTechnologyLabel}>
            Edify utiliza tecnología de OpenAI
          </div>
        )}

        <div className={styles.navIcons}>
          {/* Botón de cerrar modo conversacional - Solo visible en dashboard */}
          {isDashboardRoute && isConversationalModeActive && onCloseConversationalMode && (
            <button
              className={styles.conversationalActionButton}
              onClick={onCloseConversationalMode}
              type="button"
              aria-label="Cerrar conversación"
              title="Cerrar conversación"
            >
              <ArrowLeft size={18} />
              Volver
            </button>
          )}
          {isGuestAccount && (
            <button
              type="button"
              className={styles.guestCtaChip}
              onClick={() => openSuscriptionModal()}
            >
              Ingresaste como invitado · Crear cuenta
            </button>
          )}
          {showJobboardTrialHint && (
            <button
              type="button"
              className={`${styles.guestCtaChip} ${styles.jobboardTrialCtaChip}`}
              disabled={isJobboardUpgradeLoading}
              onClick={handleJobboardUpgrade}
            >
              {isJobboardUpgradeLoading
                ? "Activando…"
                : "Cuenta bolsa de trabajo · Activar free trial"}
            </button>
          )}
          {isDashboardRoute && !isConversationalModeActive && totalChatsCount > 0 && onOpenSearchModal && (
          <div
            className={styles.historyButtonNav}
            onMouseEnter={() => handleTooltip("historyButton")}
            onMouseLeave={() => handleTooltip("historyButton")}
          >
            <button
              className={styles.historyButtonNavBtn}
              onClick={onOpenSearchModal}
              type="button"
              aria-label="Ver conversaciones anteriores"
            >
              <MessageSquare size={20} className={styles.historyButtonIcon} />
              <span className={styles.historyBadgeNav}>{totalChatsCount}</span>
              {showToolTip.historyButton.show && (
                <Tooltip text={showToolTip.historyButton.text} fromNav={false} fromHeaderNav={true} />
              )}
            </button>
          </div>
        )}
          <div
            className={`${styles.navIcon} ${isActive === "FAQ" && styles.isActive}`}
            onMouseEnter={() => handleTooltip("faq")}
            onMouseLeave={() => handleTooltip("faq")}
            onClick={() => handleRedirect("FAQ")}
          >
            {isActive === "FAQ" ? (
              <FaqActive width={20} height={20} />
            ) : (
              <Faq width={20} height={20} className={styles.faqIcon} />
            )}
            {showToolTip.faq.show && <Tooltip text={showToolTip.faq.text} fromNav={false} fromHeaderNav={true} />}
          </div>

          <div
            className={styles.notificationWrapper}
            onMouseEnter={() => handleTooltip("notifications")}
            onMouseLeave={() => handleTooltip("notifications")}
          >
            <NotificationsImproved
              isMobile={false}
            />
            {showToolTip.notifications.show && (
              <Tooltip text={showToolTip.notifications.text} fromNav={false} fromHeaderNav={true} />
            )}
          </div>

          <div
            className={styles.userMenu}
            ref={userMenuRef}
            onMouseEnter={() => handleTooltip("userAvatar")}
            onMouseLeave={() => handleTooltip("userAvatar")}
          >
            <button
              className={`${styles.userButton} ${showUserMenu ? styles.active : ""} ${isViewingOwnProfile ? styles.profileActive : ""}`}
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label={userName}
            >
              <div className={styles.userAvatarWrapper}>
                <div className={styles.userAvatar}>
                {!imageError && profileImage ? (
                  <img
                    className={styles.avatarImage}
                    src={profileImage}
                    alt="user"
                  />
                ) : initials ? (
                  <div className={styles.avatarInitials}>{initials}</div>
                ) : (
                  <NoImage width={24} height={24} />
                )}
              </div>
              {isGuestAccount && (
                <span className={styles.guestBadgeLabel}>Invitado</span>
              )}
              {showJobboardTrialHint && (
                <span className={styles.guestBadgeLabel}>Bolsa</span>
              )}
              </div>
              {showToolTip.userAvatar.show && (
                <Tooltip text={userAvatarTooltip} fromNav={false} fromHeaderNav={true} />
              )}
            </button>

            {showUserMenu && (
              <div className={styles.userDropdown}>
                {isGuestAccount && (
                  <>
                    <div className={styles.guestCtaBlock}>
                      <span className={styles.guestCtaLabel}>Cuenta de invitado</span>
                      <p className={styles.guestCtaText}>Crear cuenta es gratis para guardar tu progreso</p>
                      <button
                        type="button"
                        className={styles.guestCtaButton}
                        onClick={() => {
                          setShowUserMenu(false)
                          openSuscriptionModal()
                        }}
                      >
                        <UserPlus size={16} />
                        Crear cuenta gratis
                      </button>
                    </div>
                    <div className={styles.dropdownDivider} />
                  </>
                )}
                {showJobboardTrialHint && (
                  <>
                    <div className={styles.guestCtaBlock}>
                      <span className={styles.guestCtaLabel}>Cuenta bolsa de trabajo</span>
                      <p className={styles.guestCtaText}>
                        Tenés el free trial disponible para ser miembro completo y usar todas las funciones de Edify.
                      </p>
                      <button
                        type="button"
                        className={styles.guestCtaButton}
                        disabled={isJobboardUpgradeLoading}
                        onClick={handleJobboardUpgrade}
                      >
                        <Sparkles size={16} />
                        {isJobboardUpgradeLoading ? "Activando…" : "Activar free trial"}
                      </button>
                    </div>
                    <div className={styles.dropdownDivider} />
                  </>
                )}
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownAvatar}>
                    {!imageError && profileImage ? (
                      <img src={profileImage} alt="user" />
                    ) : initials ? (
                      <div className={styles.dropdownInitials}>{initials}</div>
                    ) : (
                      <NoImage width={40} height={40} />
                    )}
                  </div>
                  <div className={styles.dropdownUserInfo}>
                    <p className={styles.dropdownName}>{userName}</p>
                    <p className={styles.dropdownEmail}>{userData?.username}</p>
                  </div>
                </div>

                <div className={styles.dropdownDivider} />

                <button
                  className={styles.dropdownItem}
                  onClick={handleRedirectToProfile}
                >
                  <User size={16} />
                  <span>Mi perfil</span>
                </button>

                <div className={styles.dropdownDivider} />

                <button
                  className={styles.dropdownItem}
                  onClick={handleCloseSesion}
                >
                  <LogOut size={16} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

const mapStateToProps = (state: any) => {
  return {
    userData: state.general.userData,
    isGuestAccount: state.general.userData?.username === "invited@edify.la",
    projectDocumentation: state.anteProyecto,
  }
}

export default connect(mapStateToProps, {
  closeSuscriptionModal,
  setActivateFreeTrial,
  logout,
  openMainModal,
  closeMainModal,
  openFeedTextModal,
  openSuscriptionModal,
  upgradeJobboardToClientAndActivateTrial,
})(HeaderNavImproved)

